<script setup lang="ts">
    import { onMounted, inject, reactive } from 'vue';
    import { useRouter } from 'vue-router';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { formatNumber } from '@cwd/Shared/utils/formatNumber';
    import Loading from '@shared/components/Loading.vue';
    import List from '@shared/components/List/List.vue';
    import CopyButton from '@shared/components/CopyButton.vue';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import SubscriptionAddons from '@backoffice/SubscriptionAddons/SubscriptionAddons.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { AccountBalance, SubscriptionAccess, getSubscriptionStatusClass } from '@views/BackOffice/Subscription/Subscription.access';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { getAccountStatusClass } from '@cwd/Views/BackOffice/Account/Account.access';
    import { getInvoices, AccountInvoice } from '@account/AccountInvoices/AccountInvoices.access';
    import InvoicesTable from '@account/AccountInvoices/InvoicesTable.vue';
    import { formatMoney } from '@cwd/Shared/utils/formatMoney';
    import SubscriptionTab from './components/SubscriptionTab.vue';
    import CompanyTab from './components/CompanyTab.vue';
    import BillingTab from './components/BillingTab.vue';
    import CreateAccount from './components/CreateAccount.vue';
    import SmsPrePaidTransactionTab from './components/SmsPrePaidTransactionTab.vue';
    import TemplateCopyTab from './components/TemplateCopyTab.vue';
    import FieldsetWrapper from '@shared/components/Form/FieldsetWrapper.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';

    const router = useRouter();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const props = defineProps<{
        serviceSubscriptionId: string;
        isModal: boolean;
    }>();

    const backRouteLabels: Record<string, string> = {
        SubscriptionList: 'Subscriptions',
        AccountList: 'Accounts',
        AccountEdit: 'Account',
        BackOfficeDashboard: 'Dashboard',
    };

    const backRouteName = window.history.state?.back ? router.resolve(window.history.state.back).name?.toString() : undefined;
    const backLabel = (backRouteName && backRouteLabels[backRouteName]) || 'Subscriptions';

    function goBack(): void {
        if (window.history.state?.back) router.back();
        else router.push({ name: 'SubscriptionList' });
    }

    const state = reactive<{
        invoices: AccountInvoice[] | null;
        selectedBalance: AccountBalance | null;
        balanceAmount: number;
        balanceCurrecyCode: string;
        loadingInvoices: boolean;
        showCreateAccountForm: boolean;
    }>({
        invoices: null,
        selectedBalance: null,
        balanceAmount: 0,
        balanceCurrecyCode: 'SEK',
        loadingInvoices: false,
        showCreateAccountForm: false,
    });

    onMounted(async () => {
        state.loadingInvoices = true;

        await Promise.all([
            subscriptionAccess.getServiceSubscription(props.serviceSubscriptionId).then(async () => {
                await Promise.all([
                    subscriptionAccess.getClientCompany(subscriptionAccess.state.boServiceSubscription!.ClientCompanyId),
                    subscriptionAccess.getClientCompanyAccounts(),
                    subscriptionAccess.getOrders(),
                    subscriptionAccess.getMonthlyPriceWithAddons(),
                    subscriptionAccess.getMonthlyPriceBreakdown(),
                    subscriptionAccess.getRenewalExclusionReason(),
                ]);
            }),
            subscriptionAccess.getAccountBalances(props.serviceSubscriptionId),
            getInvoices(props.serviceSubscriptionId, true)
                .then((invoices) => {
                    state.invoices = invoices;
                })
                .finally(() => {
                    state.loadingInvoices = false;
                }),
            subscriptionAccess.getOpenAIConsumption(props.serviceSubscriptionId),
        ]);
    });

    function selectAccountBalance(balance: AccountBalance) {
        state.selectedBalance = balance;
    }

    async function giveAwayMoney() {
        await subscriptionAccess.giveAwayMoney(state.selectedBalance!, state.balanceAmount, state.balanceCurrecyCode).then(() => {
            state.balanceAmount = 0;
            state.selectedBalance = null;
        });
    }

    function handleAccountCreated() {
        state.showCreateAccountForm = false;
    }

    function handleCancelled() {
        state.showCreateAccountForm = false;
    }
</script>

<template>
    <div v-if="featureAccess.hasBackOfficeAccess()">
        <div v-if="!props.isModal">
            <p class="fw-600 m-lg-t m-xs-b">
                <a href="javascript:void(0)" role="button" @click="goBack">
                    <span class="glyphicon glyphicon-chevron-left m-xxs-r c-light" aria-hidden="true"></span>
                    {{ backLabel }}
                </a>
                <template v-if="backLabel !== 'Subscriptions'">
                    |
                    <router-link :to="{ name: 'SubscriptionList' }">All subscriptions</router-link>
                </template>
            </p>
        </div>

        <Loading
            :show="subscriptionAccess.state.loadingBOClientCompany || subscriptionAccess.state.loadingBOServiceSubscription"
            loadingStyle="progress-bar"
            :fixed="true" />

        <template v-if="subscriptionAccess.state.boServiceSubscription != null && subscriptionAccess.state.boClientCompany != null">
            <div class="m-lg-b">
                <h2 class="h3">
                    <i class="fa fa-square" aria-hidden="true" :class="getSubscriptionStatusClass(subscriptionAccess.state.boServiceSubscription)"></i>
                    {{ subscriptionAccess.state.boClientCompany.Name }}
                </h2>

                <p>
                    {{ subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.ServiceSubscriptionTier.Name }}
                    {{ formatNumber(subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.ServiceLevel.ContactLimit) }}

                    <span v-if="!subscriptionAccess.state.loadingMonthlyPriceWithAddons">
                        | {{ formatNumber(subscriptionAccess.state.monthlyPriceWithAddons) }}
                        {{ subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode }}
                    </span>
                </p>

                <CopyButton
                    buttonText="Copy service subscription id"
                    :textToCopy="subscriptionAccess.state.boServiceSubscription.ServiceSubscriptionId"
                    :tooltip="subscriptionAccess.state.boServiceSubscription.ServiceSubscriptionId" />
                <CopyButton
                    class="m-xs-l"
                    buttonText="Copy client company id"
                    :textToCopy="subscriptionAccess.state.boServiceSubscription.ClientCompanyId"
                    :tooltip="subscriptionAccess.state.boServiceSubscription.ClientCompanyId" />
            </div>

            <tabs>
                <tab title="Accounts">
                    <h3 class="m-lg-b">Accounts</h3>

                    <Loading :show="subscriptionAccess.state.loadingAccounts" loadingStyle="progress-bar" :fixed="true" />

                    <CreateAccount v-if="state.showCreateAccountForm" @cancelled="handleCancelled" :onComplete="handleAccountCreated" />

                    <List
                        v-if="!subscriptionAccess.state.loadingAccounts"
                        displayAs="table"
                        itemId="AccountId"
                        :items="subscriptionAccess.state.accounts"
                        :tableHeaders="['Account', 'Created', 'Contact count', 'User count']"
                        orderByValue="ContactCount"
                        :orderOptions="[
                            { display: 'Contact count', key: 'ContactCount' },
                            { display: 'Created', key: 'Created' },
                            { display: 'User count', key: 'UserCount' },
                        ]"
                        orderByDirection="desc"
                        :filter-keys="['Name']"
                        class="th3-text-right th4-text-right">
                        <template #toolbar>
                            <button
                                v-if="!subscriptionAccess.state.loadingAccounts && !state.showCreateAccountForm"
                                type="button"
                                class="btn btn-default"
                                @click="
                                    () => {
                                        state.showCreateAccountForm = true;
                                    }
                                ">
                                Create account
                            </button>
                        </template>
                        <template #item="{ item: row, index: index }">
                            <td>
                                <i class="fa fa-square m-sm-r" aria-hidden="true" :class="getAccountStatusClass(row)"></i>
                                <router-link :to="{ name: 'AccountEdit', params: { accountId: row.AccountId } }">
                                    {{ row.Name }}
                                </router-link>
                            </td>
                            <td>
                                <time :datetime="row.Created">{{ formatDateTime(row.Created) }}</time>
                            </td>
                            <td class="text-right">
                                {{ formatNumber(row.ContactCount) }}
                            </td>
                            <td class="text-right">
                                {{ formatNumber(row.UserCount) }}
                            </td>
                        </template>
                    </List>
                </tab>
                <tab title="Subscription">
                    <h3 class="m-lg-b">Subscription</h3>
                    <SubscriptionTab :service-subscription-id="props.serviceSubscriptionId" />
                </tab>
                <tab title="Company">
                    <h3 class="m-lg-b">Company</h3>
                    <CompanyTab />
                </tab>
                <tab title="Features & addons">
                    <h3 class="m-lg-b">Features & addons</h3>
                    <SubscriptionAddons :serviceSubscriptionId="props.serviceSubscriptionId"></SubscriptionAddons>
                </tab>
                <tab title="Account balances">
                    <h3 class="m-lg-b">Account balances</h3>

                    <Loading :show="subscriptionAccess.state.loadingAccountBalances" loadingStyle="progress-bar" :fixed="true" />

                    <template v-if="!subscriptionAccess.state.loadingAccountBalances">
                        <div v-if="state.selectedBalance" class="fieldset-grid">
                            <FieldsetWrapper :legend="'Selected: ' + state.selectedBalance.Name" class="fieldset-grid">
                                <FormInput class="form-group" v-model:modelValue.number="state.balanceAmount" label="Amount" type="number" :min="0"></FormInput>

                                <FormSelect
                                    class="form-group"
                                    v-model:modelValue="state.balanceCurrecyCode"
                                    label="Currency"
                                    :options="subscriptionAccess.state.currencyCodes.map((currency) => ({ label: currency, value: currency }))"></FormSelect>

                                <div class="form-group">
                                    <button class="btn btn-primary" :disabled="state.balanceAmount <= 0" @click="giveAwayMoney()">Make it rain!</button>
                                </div>
                            </FieldsetWrapper>
                        </div>

                        <img
                            v-if="subscriptionAccess.state.gaveAwayMoney && !state.selectedBalance"
                            class="img-responsive m-sm-b"
                            :src="'/Content/img/mir.gif'"
                            alt="Make it rain!" />

                        <List
                            displayAs="table"
                            :items="subscriptionAccess.state.accountBalances"
                            :tableHeaders="['Account', 'Balance']"
                            :filter-keys="['Name']"
                            class="th2-text-right">
                            <template #item="{ item: balance, index }">
                                <td>
                                    <strong v-if="state.selectedBalance == balance">
                                        <template v-if="balance.Name">{{ balance.Name }}</template>
                                        <template v-else>Subscription</template>
                                    </strong>
                                    <a v-else href="javascript:void(0)" @click="selectAccountBalance(balance)">
                                        <template v-if="balance.Name">{{ balance.Name }}</template>
                                        <template v-else>Subscription</template>
                                    </a>
                                </td>
                                <td class="text-right">
                                    <template v-if="balance.Balance == null || balance.CurrencyCode == null">0</template>
                                    <template v-else>{{ formatMoney(balance.Balance, balance.CurrencyCode) }}</template>
                                </td>
                            </template>
                        </List>
                    </template>
                </tab>
                <tab title="Transactions">
                    <h3 class="m-lg-b">Transactions</h3>

                    <Loading :show="subscriptionAccess.state.loadingOrders" loadingStyle="progress-bar" :fixed="true" />

                    <List
                        v-if="!subscriptionAccess.state.loadingOrders"
                        displayAs="table"
                        :items="subscriptionAccess.state.orders"
                        :tableHeaders="['Created', 'Status', 'Type', 'Amount']"
                        class="th4-text-right"
                        :filter-keys="['Created', 'OrderStatus', 'OrderType']"
                        orderByValue="Created"
                        :orderOptions="[
                            { display: 'Created', key: 'Created' },
                            { display: 'Status', key: 'Status' },
                            { display: 'Type', key: 'Type' },
                            { display: 'Amount', key: 'Amount' },
                        ]"
                        orderByDirection="desc">
                        <template #item="{ item: order, index }">
                            <td>{{ formatDateTime(order.Created) }}</td>
                            <td>
                                <i
                                    v-if="order.OrderStatus"
                                    class="fa fa-square m-sm-r"
                                    aria-hidden="true"
                                    :class="{ 'status-ok': order.OrderStatus == 'Paid', 'status-not-ok': order.OrderStatus != 'Paid' }"></i>
                                <span>{{ order.OrderStatus }}</span>
                            </td>
                            <td>{{ order.OrderType }}</td>
                            <td class="text-right">
                                {{ formatMoney(order.Amount, order.CurrencyCode) }}
                            </td>
                        </template>
                    </List>
                </tab>
                <tab title="Invoices">
                    <h3 class="m-lg-b">{{ $Resources.InvoicesTab }}</h3>

                    <Loading :show="state.loadingInvoices" loadingStyle="progress-bar" :fixed="true" />

                    <InvoicesTable
                        v-if="!state.loadingInvoices"
                        :invoices="state.invoices"
                        :serviceSubscriptionId="props.serviceSubscriptionId"></InvoicesTable>
                </tab>
                <tab title="Billing info">
                    <h3 class="m-lg-b">Billing Information</h3>
                    <BillingTab />
                </tab>
                <tab title="SMS PrePaidTransaction">
                    <SmsPrePaidTransactionTab :service-subscription-id="props.serviceSubscriptionId" />
                </tab>
                <tab title="Copy templates">
                    <h3 class="m-lg-b">Copy templates between accounts</h3>
                    <TemplateCopyTab
                        :client-company-id="subscriptionAccess.state.boServiceSubscription!.ClientCompanyId"
                        :service-subscription-id="props.serviceSubscriptionId" />
                </tab>
            </tabs>
        </template>
    </div>
</template>

<style scoped>
    .fieldset-grid {
        max-width: 800px;
    }
</style>
